import React, { useState, useEffect } from "react";
import { Tooltip } from "./Tooltip";

interface ToggleSwitchProps {
  enabled: boolean;
  onToggle: () => void;
  label: string | React.ReactElement;
  tooltip?: React.ReactNode;
  tooltipShortcut?: string;
  tooltipAlwaysVisible?: boolean;
  disabled?: boolean;
  variant?: "default" | "compact";
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  enabled,
  onToggle,
  label,
  tooltip,
  tooltipShortcut,
  tooltipAlwaysVisible = false,
  disabled = false,
  variant = "default",
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const [mobileTooltipTimeoutId, setMobileTooltipTimeoutId] =
    useState<NodeJS.Timeout | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setIsAnimating(true);
    onToggle();
  };

  // Mobile-specific click handler for label/background
  const handleMobileLabelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || !isMobile || !tooltip) return;

    // Check if the click target is the toggle button itself
    const target = event.target as HTMLElement;
    if (target.tagName === "BUTTON" || target.closest("button")) {
      return; // Don't show/hide tooltip if the actual toggle button was clicked
    }

    // Prevent the click from bubbling to the toggle button
    event.stopPropagation();

    // Clear any existing timeout
    if (mobileTooltipTimeoutId) {
      clearTimeout(mobileTooltipTimeoutId);
      setMobileTooltipTimeoutId(null);
    }

    // Toggle tooltip visibility
    setShowMobileTooltip((prev) => {
      const newState = !prev;

      // If showing tooltip, set a 5-second auto-hide timer
      if (newState) {
        const timeoutId = setTimeout(() => {
          setShowMobileTooltip(false);
          setMobileTooltipTimeoutId(null);
        }, 5000);
        setMobileTooltipTimeoutId(timeoutId);
      }

      return newState;
    });
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        "ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          window.innerWidth <= 768
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 100); // Animation duration - shorter than position transition
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mobileTooltipTimeoutId) {
        clearTimeout(mobileTooltipTimeoutId);
      }
    };
  }, [mobileTooltipTimeoutId]);

  const isCompact = variant === "compact";

  const containerStyle = isCompact
    ? {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 14px",
        background: "var(--overlay-bg)",
        borderRadius: "12px",
        userSelect: "none" as const,
      }
    : {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "16px 24px",
        background: "var(--toggle-bg)",
        borderRadius: "12px",
        border: "none",
        backdropFilter: "blur(7px)",
        position: "relative" as const,
        userSelect: "none" as const,
      };

  const labelStyle = isCompact
    ? {
        fontSize: "13px",
        opacity: disabled ? 0.4 : 0.8,
        color: "var(--text-secondary)",
        cursor: "default",
        userSelect: "none" as const,
        transition: "opacity 0.3s ease",
      }
    : {
        fontSize: "14px",
        color: "var(--text-secondary)",
        opacity: disabled ? 0.5 : 1,
        cursor: "default",
        userSelect: "none" as const,
        transition: "opacity 0.3s ease",
      };

  const buttonStyle = isCompact
    ? {
        width: "52px",
        height: "30px",
        borderRadius: "15px",
        border: "1px solid var(--toggle-border)",
        background: disabled
          ? "rgba(75, 85, 99, 0.3)"
          : enabled
          ? "rgba(5, 150, 105, 0.35)"
          : "var(--toggle-bg)",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative" as const,
        transition: "all 0.3s ease",
      }
    : {
        width: "48px",
        height: "24px",
        borderRadius: "12px",
        border: "none",
        background: disabled
          ? "rgba(75, 85, 99, 0.3)"
          : enabled
          ? "rgba(5, 150, 105, 0.6)"
          : "rgba(75, 85, 99, 0.6)",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative" as const,
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        padding: "2px",
        overflow: "hidden",
      };

  const knobStyle = isCompact
    ? {
        position: "absolute" as const,
        top: "50%",
        transform: "translateY(-50%)",
        left: enabled ? "26px" : "4px",
        width: isAnimating ? "18px" : "22px",
        height: isAnimating ? "18px" : "22px",
        borderRadius: "50%",
        background: "var(--text-secondary)",
        transition:
          "left 0.2s ease, width 0.1s ease-in-out, height 0.1s ease-in-out",
      }
    : {
        width: isAnimating ? "16px" : "20px",
        height: isAnimating ? "16px" : "20px",
        borderRadius: "50%",
        background: "#ffffff",
        transform: enabled ? "translateX(24px)" : "translateX(0)",
        transition:
          "transform 0.2s ease, width 0.1s ease-in-out, height 0.1s ease-in-out",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
        position: "relative" as const,
        zIndex: 2,
        opacity: disabled ? 0.6 : 1,
      };

  const toggleSwitchContent = (
    <div
      className="toggle-switch"
      style={containerStyle}
      onClick={isMobile ? handleMobileLabelClick : undefined}
    >
      <span style={labelStyle}>{label}</span>
      <button
        onClick={handleClick}
        disabled={disabled}
        style={buttonStyle}
        onMouseEnter={
          isCompact
            ? (e) => {
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.2)";
              }
            : undefined
        }
        onMouseLeave={
          isCompact
            ? (e) => {
                e.currentTarget.style.boxShadow = "none";
              }
            : undefined
        }
      >
        {isCompact ? <span style={knobStyle} /> : <div style={knobStyle} />}
      </button>
    </div>
  );

  // For mobile, we need custom tooltip behavior
  if (tooltip && isMobile) {
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        {toggleSwitchContent}
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: showMobileTooltip
              ? "translateX(-50%) translateY(0) scale(1)"
              : "translateX(-50%) translateY(10px) scale(0.95)",
            padding: "8px 12px",
            background: "var(--toggle-bg)",
            color: "var(--text-secondary)",
            borderRadius: "12px",
            fontSize: "clamp(11px, 3vw, 14px)",
            fontWeight: "normal",
            textAlign: "center",
            whiteSpace: "normal",
            maxWidth: "200px",
            width: "max-content",
            zIndex: 1000,
            border: "none",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
            lineHeight: "1.4",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            pointerEvents: "none",
            userSelect: "none",
            opacity: showMobileTooltip ? 1 : 0,
          }}
        >
          {tooltipShortcut ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <kbd
                style={{
                  background: "var(--kbd-bg)",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "11px",
                  fontFamily: "monospace",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {tooltipShortcut}
              </kbd>
              <span style={{ flex: 1 }}>{tooltip}</span>
            </div>
          ) : (
            tooltip
          )}
        </div>
      </div>
    );
  }

  return tooltip ? (
    <Tooltip
      text={tooltip}
      position="top"
      alwaysVisible={tooltipAlwaysVisible}
      shortcut={tooltipShortcut}
    >
      {toggleSwitchContent}
    </Tooltip>
  ) : (
    toggleSwitchContent
  );
};
